import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import * as messageService from '../services/messageService';

interface NotificationContextType {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
  decrementUnreadCount: () => void;
  incrementUnreadCount: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { authState } = useAuth();
  const { isAuthenticated, user } = authState;

  const refreshUnreadCount = useCallback(async () => {
    if (isAuthenticated && user?.role === 'student') {
      try {
        const response = await messageService.getUnreadMessageCount();
        setUnreadCount(response.data.unreadCount);
      } catch (error) {
        console.error('Error fetching unread message count:', error);
      }
    } else {
      setUnreadCount(0);
    }
  }, [isAuthenticated, user?.role]);

  const decrementUnreadCount = () => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const incrementUnreadCount = () => {
    setUnreadCount(prev => prev + 1);
  };

  useEffect(() => {
    refreshUnreadCount();

    // Set up interval to check for new messages every 30 seconds
    const interval = setInterval(refreshUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  const value = {
    unreadCount,
    refreshUnreadCount,
    decrementUnreadCount,
    incrementUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};